import { Box, Image } from '@chakra-ui/react';
import { invariant } from '@ifixit/helpers';
import { FlagCountryCode } from '@ifixit/icons';
import { useShopifyStorefrontClient } from '@ifixit/shopify-storefront-client';
import { CartDrawer, SupportLink, type SupportLinkProps, useCrossSellItems } from '@ifixit/ui';

type CartDrawerWrapperProps = {
   flagCountryCode: FlagCountryCode;
   locale: SupportLinkProps['localeCode'] | null;
};

export function CartDrawerWrapper({ flagCountryCode, locale }: CartDrawerWrapperProps) {
   const crossSellItems = useCrossSellItems();
   const { cartUrl } = useShopifyStorefrontClient();
   invariant(cartUrl, 'cart url must be provided for carpenter cart drawer');
   invariant(flagCountryCode, 'flag country code must be provided for carpenter cart drawer');
   invariant(locale, 'locale must be provided for carpenter cart drawer');
   const UnableToFetchCartDescription = () => (
      <>
         {_js('Please try to reload the page. If the problem persists, please ')}
         <SupportLink localeCode={locale}>{_js('contact us')}</SupportLink>
      </>
   );
   return (
      <Box ml={4} mr={{ base: 3, md: 4 }}>
         <CartDrawer
            crossSellItems={crossSellItems}
            flagCode={flagCountryCode}
            buyButtonText={_js('Checkout')}
            suggestAddProductText={_js('Add the product below to your order')}
            localeCode={locale}
            ImageComponent={CartImage}
            emptyCartText={_js('Your cart is empty')}
            backToShoppingText={_js('Back to shopping')}
            numberOfItemsInCartText={_js('Number of Items in Cart: ')}
            unableToFetchCartText={_js('Unable to fetch the cart')}
            unableToFetchCartDescription={<UnableToFetchCartDescription />}
            youSavedText={_js('You saved ')}
            viewCartText={_js('View Cart')}
            preOrderText={_js('Preorder')}
            addToCartText={_js('Add to cart')}
            formatDiscountLabel={discount => `${discount}${_js('% Off')}`}
            updateCartTranslations={{
               ariaDecrease: _js('Decrease quantity by one'),
               ariaIncrease: _js('Increase quantity by one'),
               notEnoughInventory: _js('Not enough inventory'),
               outOfStock: _js('Out of stock'),
               unableToUpdateQuantity: _js('Unable to update quantity.'),
            }}
            useAddToCartTranslations={{
               unableToAddProduct: _js('Unable to add product'),
               tryActionAgain: _js('Please try again. If the problem persists contact us.'),
            }}
            useRemoveFromCartTranslations={{
               unableToRemoveProduct: _js('Unable to remove product'),
               tryActionAgain: _js('Please try again. If the problem persists contact us.'),
            }}
            viewCartUrl={cartUrl}
         />
      </Box>
   );
}

function CartImage({ alt, src }: { alt?: string; src: string }) {
   return <Image src={src} alt={alt} objectFit="cover" />;
}
